import { getTimeRemaining } from 'utilities/date';

/**
 * Renders a countdown timer into the specified element.
 * @param {HTMLElement} element The element to render the countdown into.
 * @param {Date} endtime The end time of the countdown.
 * @param {Date} startServerTime The server time at the start of the countdown.
 * @param {Function} done The callback function to call when the countdown reaches zero.
 * @param {string} customPretext The custom pretext to display before the countdown.
 * @param {boolean} collapseDays Whether to collapse days into hours.
 * @param {boolean} useCompactFormat Whether to use compact format (`true`) or verbose format (`false`).
 *     - If `true, the format will be like `02:06:52:04`.
 *     - If `false`, the format will be like `2d 6hs 52m 4s`.
 * @returns The formatted countdown string.
 */
function renderPromoCountdown (
	element,
	endtime,
	startServerTime,
	done,
	customPretext,
	collapseDays = false,
	useCompactFormat = false
) {

	if (!element) {

		return;

	}

	const formatCountdown = (t) => {

		let days = t.days;
		let hours = t.hours;
		const minutes = t.minutes;
		const seconds = t.seconds;

		if (collapseDays) {

			hours = (days * 24) + hours;
			days = 0;

		}

		// Ordered from most → least significant
		const units = [
			{ key: 'd', val: days },
			{ key: 'hs', val: hours },
			{ key: 'm', val: minutes },
			{ key: 's', val: seconds }
		];

		// Find the first non-zero unit
		const start = units.findIndex((u) => u.val > 0);

		// If everything is zero, show just "0s"
		if (start === -1) {

			const onlySeconds = useCompactFormat ? `${collapseDays ? '' : '00:'}00:00:00` : '<strong>0</strong>s';

			return typeof customPretext === 'string' ? `${customPretext} ${onlySeconds}` : `Ends in ${onlySeconds}`;

		}

		let timeString;

		if (useCompactFormat) {

			// Keep zeros, pad to two digits
			const parts = units.map((u) => u.val.toString().padStart(2, '0'));
			timeString = parts.join(':');

		} else {

			// Build from first non-zero to the end, keeping zeros that follow
			const parts = units.slice(start).map((u) => `<strong>${u.val}</strong>${u.key}`);
			timeString = parts.join(' ');

		}

		return typeof customPretext === 'string' ? `${customPretext} ${timeString}` : `Ends in ${timeString}`;

	};

	const initializeClock = () => {

		const countDownElement = element;
		let timeinterval;
		let currentTime = new Date(startServerTime);

		const updateClock = () => {

			const timeRemaining = getTimeRemaining(endtime, new Date(currentTime).getTime());
			currentTime = new Date(currentTime.getTime() + 1000);

			countDownElement.innerHTML = formatCountdown(timeRemaining);

			if (timeRemaining.total <= 0) {

				clearInterval(timeinterval);
				done?.();

			}

		};

		updateClock();
		timeinterval = setInterval(updateClock, 1000);

	};

	initializeClock();

}

export default renderPromoCountdown;
